import { forwardRef, ForwardedRef } from "react";
import { Grid2 as Grid, Typography, Box, Modal as MuiModal, Button, Stack } from "@mui/material";
import { useTranslate } from "@refinedev/core";

import { Form } from "../Form";
import { useModalController } from "./controller";
import { ModalProps, ModalRef } from "./types";
import { modalStyle } from "./styles";

export const Modal = forwardRef((
  {
    title,
    methods,
    children,
    confirmLabel,
    cancelLabel,
    isPending,
    showForm = !!methods,
    hideConfirmButton = false,
    hideCancelButton = false,
    customActions,
    onConfirm,
    onClose,
    onOpen
  }: ModalProps,
  ref: ForwardedRef<ModalRef>
) => {
  const translate = useTranslate();

  const {
    isOpen,
    handleClose,
    handleConfirm
  } = useModalController(ref, {
    mutate: onConfirm,
    reset: methods?.reset,
    onClose,
    onOpen
  });

  const renderContent = () => (
    <>
      {title && (
        <Grid size={12}>
          <Typography style={{ fontSize: 22, color: "primary.main", marginBottom: 20 }}>
            {title}
          </Typography>
        </Grid>
      )}

      <Grid size={12}>
        {children}
      </Grid>
    </>
  );

  const renderActions = () => {
    if (customActions) return customActions;

    return (
      <Stack direction="row" spacing={2} justifyContent="flex-end" width="100%">
        {!hideCancelButton && (
          <Button type="button" onClick={handleClose} disabled={isPending}>
            {cancelLabel || translate("buttons.cancel")}
          </Button>
        )}

        {!hideConfirmButton && onConfirm && (
          <Button
            type={showForm && methods ? "submit" : "button"}
            variant="contained"
            onClick={showForm && methods ? undefined : handleConfirm}
            disabled={isPending}
            loading={isPending}
          >
            {confirmLabel || translate("buttons.save")}
          </Button>
        )}
      </Stack>
    );
  };

  return (
    <MuiModal open={isOpen} onClose={handleClose} keepMounted>
      <Box sx={modalStyle}>
        <Grid container spacing={3}>
          {showForm && methods ? (
            <Form methods={methods} onSubmit={handleConfirm}>
              {renderContent()}

              <Grid size={12} sx={{ mt: 3 }}>
                {renderActions()}
              </Grid>
            </Form>
          ) : (
            <>
              {renderContent()}
              <Grid size={12}>
                {renderActions()}
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </MuiModal>
  );
});
